import {
	RadioGroup as HeadlessRadioGroup,
	type RadioGroupProps as HeadlessRadioGroupProps,
} from "@headlessui/react";
import { cva, type VariantProps } from "class-variance-authority";
import { type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

const groupVariants = cva("flex w-fit", {
	variants: {
		direction: {
			row: "flex-row",
			column: "flex-col",
		},
		gap: {
			sm: "gap-1.5",
			md: "gap-2.5",
			lg: "gap-4",
		},
	},
	defaultVariants: { direction: "row", gap: "md" },
});

type GroupVariantProps = VariantProps<typeof groupVariants>;

export type RadioGroupProps<TType> = Omit<HeadlessRadioGroupProps<"div", TType>, "className"> &
	GroupVariantProps & {
		className?: string;
		/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
		trackingId?: string;
		ref?: Ref<HTMLDivElement>;
	};

export const RadioGroup = <TType,>({
	direction,
	gap,
	trackingId,
	className,
	ref,
	...rest
}: RadioGroupProps<TType>) => (
	<HeadlessRadioGroup
		ref={ref}
		className={cn(groupVariants({ direction, gap }), className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
